import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import { Button, MenuItem, TextField, Tooltip, CircularProgress } from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import ItemisedBillReportService from 'services/ItemisedBillReportService';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import AlertHelper from 'helpers/AlertHelper';
import Error from 'components/utils/Alert/Error';
import { ShopContext } from 'Context/ShopContext';
import { AlertMessagesContext } from 'react-alert-messages';
import CsvHelper from 'helpers/CsvHelper';
import { filterEndDate } from 'components/utils/FilterEndDate';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { InventoryContext } from 'Context/InventoryContext';
import { useTranslation } from 'react-i18next';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { PRINT_TYPES } from 'const';

function HSNSummaryReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { getCategories, categories } = useContext(InventoryContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [filterCategory, setFilterCategory] = useState('all');
  const [reportDataB2C, setReportDataB2C] = useState([]);
  const [reportDataB2B, setReportDataB2B] = useState([]);
  const [filteredReportDataB2B, setFilteredReportDataB2B] = useState([]);
  const [filteredReportDataB2C, setFilteredReportDataB2C] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    filterCategory,
  });
  const [errorMsg, setErrorMsg] = useState();
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    hsn: tCommon('HSN'),
    unit: tCommon('Unit'),
    quantity: tCommon('Quantity'),
    taxable_amount: {
      title: tCommon('Taxable Amount'),
      getValue: (datum) => `${ShopsHelper.getAmountFormatted(shop, datum.total - (datum.tax + datum.cess))}`,
    },
    tax_percentage: tCommon('Tax(%)'),
    tax: {
      title: tCommon('Tax Amount'),
      getValue: (datum) => `${ShopsHelper.getAmountFormatted(shop, datum.tax)}`,
    },
    cess_percentage: tCommon('Additional Tax(%)'),
    cess: {
      title: tCommon('Additional Tax'),
      getValue: (datum) => `${ShopsHelper.getAmountFormatted(shop, datum.cess)}`,
    },
    total: {
      title: tCommon('Total Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total || 0);
      },
    },
  };

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      filterCategory !== prevFilterData.filterCategory
    );
  }, [
    filterFromDate,
    filterToDate,
    filterCategory,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.filterCategory,
  ]);

  useEffect(() => {
    generateItemisedBillReports();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isShowGenerateButton, isAnyChangeOnReportsFilters]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('HSN Tax Summary Report'),
      filterFromDate,
      filterToDate,
      headerData,
      [...filteredReportDataB2B, ...filteredReportDataB2C],
      {
        [tReport('Business (B2B) Total')]: summaryB2B.totalAmount,
        [tReport('Customer (B2C) Total')]: summaryB2C.totalAmount,
        [tCommon('Total Tax Amount')]: ShopsHelper.getAmountFormatted(shop, 
          summaryB2B.totalTaxAmountNumeric + summaryB2C.totalTaxAmountNumeric
        ),
      }
    );
  };

  const generateItemisedBillReports = () => {
    const { startDate, endDate } = dynamicDateRange('HSN_SUMM_REPORT');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadItemisedBillReports({
      startTime: startDate,
      endTime: endDate,
      category: filterCategory,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      filterCategory,
    });
  };

  const toCsvBtnPressed = async () => {
    const dateTime = DateTimeHelpers.getDate(new Date());

    let combinedCsvParts = [];

    if (reportDataB2B && reportDataB2B.length > 0) {
      const b2bTitle = `HSN Report - Business(B2B)`;
      const b2bCsv = CsvHelper.getString(reportDataB2B, CSV_COLUMNS, b2bTitle);
      combinedCsvParts.push(b2bCsv);
    }

    if (reportDataB2C && reportDataB2C.length > 0) {
      const b2cTitle = `HSN Report - Customer (B2C)`;
      const b2cCsv = CsvHelper.getString(reportDataB2C, CSV_COLUMNS, b2cTitle);
      if (combinedCsvParts.length > 0) {
        combinedCsvParts.push('', '');
      }
      combinedCsvParts.push(b2cCsv);
    }

    if (combinedCsvParts.length === 0) {
      postAlertMessage({
        text: tReport('No data available to export.'),
        type: 'warning',
      });
      return;
    }

    const combinedCsv = combinedCsvParts.join('\n');

    const blob = new Blob([combinedCsv], {
      type: 'text/csv;charset=utf-8;',
    });

    saveAs(blob, `HSN-WISE-REPORT-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const loadItemisedBillReports = async ({
    startTime = filterFromDate,
    endTime = filterToDate,
    category = filterCategory,
  } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }
    setLoadingIndicator(true);
    try {
      const timezoneFreeStartTime = DateTimeHelpers.getTimezoneOffsetDate(startTime);
      const timezoneFreeEndTime = DateTimeHelpers.getTimezoneOffsetDate(endTime);

      const data = {
        filter_start_time: timezoneFreeStartTime,
        filter_end_time: timezoneFreeEndTime,
        category_id: category === 'all' ? undefined : category,
      };

      const resp = await ItemisedBillReportService.getHSNReport(data);
      const b2bData = resp.filter((item) => item.sale_type === 'B2B');
      const b2cData = resp.filter((item) => item.sale_type === 'B2C');

      setReportDataB2B(b2bData);
      setReportDataB2C(b2cData);
      setFilteredReportDataB2B(b2bData);
      setFilteredReportDataB2C(b2cData);
    } catch (err) {
      AlertHelper.setShortMessage(setErrorMsg, err.message);
    } finally {
      setLoadingIndicator(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredReportDataB2B(
      reportDataB2B.filter((item) =>
        value.length === 0 ? true : item.hsn && item.hsn.toString().includes(value.toLowerCase())
      )
    );
    setFilteredReportDataB2C(
      reportDataB2C.filter((item) =>
        value.length === 0 ? true : item.hsn && item.hsn.toString().includes(value.toLowerCase())
      )
    );
  };

  const headerData = [
    {
      label: tCommon('HSN'),
      id: 'hsn',
      type: 'text',
    },
    {
      label: tCommon('Unit'),
      id: 'unit',
      type: 'text',
    },
    {
      label: tCommon('Quantity'),
      id: 'quantity',
      type: 'callback',
      viewRender: (item) => `${ShopsHelper.getAmountFormatted(shop, item.quantity)}`
    },
    {
      label: tCommon('Taxable Amount'),
      id: 'cess',
      type: 'callback',
      viewRender: (item) => `${ShopsHelper.getAmountFormatted(shop, item.total - (item.tax + item.cess))}`,
    },
    {
      label: tCommon('Tax(%)'),
      id: 'tax_percentage',
      type: 'floatAmount',
    },
    {
      label: tCommon('Tax Amount'),
      id: 'tax',
      type: 'floatAmount',
    },
    {
      label: tCommon('Additional Tax(%)'),
      id: 'cess_percentage',
      type: 'floatAmount',
    },
    {
      label: tCommon('Additional Tax'),
      id: 'cess',
      type: 'floatAmount',
    },
    {
      label: tCommon('Total Amount'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item?.total)}`;
      },
    },
  ];

  const _getItemisedReportSummary = (items) => {
    let totalAmount = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;
    let totalTaxAmount = 0;
    items.forEach((item) => {
      totalAmount += item?.total;
      totalTax += item.tax;
      totalAdditionalTax += item.cess;
      totalTaxAmount += item.tax + item.cess;
    });
    return {
      totalAmountNumeric: totalAmount,
      totalTaxNumeric: totalTax,
      totalAdditionalTaxNumeric: totalAdditionalTax,
      totalTaxAmountNumeric: totalTaxAmount,
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
      totalTaxAmount: ShopsHelper.getAmountFormatted(shop, totalTaxAmount),
    };
  };

  const summaryB2B = _getItemisedReportSummary(filteredReportDataB2B);
  const summaryB2C = _getItemisedReportSummary(filteredReportDataB2C);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('HSN Tax Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tReport('Search by hsn')}
              />
              <Tooltip title={tCommon('Search')} placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.dateDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="HSN_SUMM_REPORT"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Category')}</label>
            </div>
            <div>
              <TextField
                select
                name="gst"
                size="small"
                variant="outlined"
                defaultValue={filterCategory}
                color="primary"
                className={styles.textfeild}
                onChange={(event) => {
                  setFilterCategory(event.target.value);
                }}
              >
                {categories?.map((category) => (
                  <MenuItem value={category.id} key={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
                <MenuItem value="all">{tCommon('All')}</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateItemisedBillReports}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {
        errorMsg && (
          <div className={styles.marginTop}>
            <Error title={errorMsg} />
          </div>
        )
      }

      {
        isShowGenerateButton === null ? (
          <Info title={tReport('Oh no, there are data matching to your filter')} content={tReport('There are no data matching to your filter. Please verify your inputs to generate report')} />
        ) : (
          filteredReportDataB2B.length === 0 &&
          filteredReportDataB2C.length === 0 &&
          !loadingIndicator && (
            <Info severity="warning" title={tReport('Oh no, there are data matching to your filter')} content={tReport('There are no data matching to your filter. Please verify your inputs to generate report')} />
          )
        )
      }
      {
        isShowGenerateButton !== null && filteredReportDataB2B && filteredReportDataB2B.length > 0 && (
          <div className={styles.tableContainer} style={{ marginTop: '20px' }}>
            <h2 className={styles.subTitle}>{tReport('Business (B2B)')}</h2>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={filteredReportDataB2B}
              rowKey="bill_no-plu"
              summary={{
                hsn: tCommon('Total'),
                total: `${shop?.currency || ''} ${summaryB2B?.totalAmount}`,
                tax: `${shop?.currency || ''} ${summaryB2B.totalTax}`,
                cess: `${shop?.currency || ''} ${summaryB2B.totalAdditionalTax}`,
                total_tax: `${shop?.currency || ''} ${summaryB2B.totalTaxAmount}`,
              }}
            />
          </div>
        )
      }

      {
        isShowGenerateButton !== null && filteredReportDataB2C && filteredReportDataB2C.length > 0 && (
          <div className={styles.tableContainer} style={{ marginTop: '20px' }}>
            <h2 className={styles.subTitle}>{tReport('Customer (B2C)')}</h2>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={filteredReportDataB2C}
              rowKey="bill_no-plu"
              summary={{
                hsn: tCommon('Total'),
                total: `${shop?.currency || ''} ${summaryB2C?.totalAmount}`,
                tax: `${shop?.currency || ''} ${summaryB2C.totalTax}`,
                cess: `${shop?.currency || ''} ${summaryB2C.totalAdditionalTax}`,
                total_tax: `${shop?.currency || ''} ${summaryB2C.totalTaxAmount}`,
              }}
            />
          </div>
        )
      }

      {
        dateLimitPopup && (
          <DateLimitDialog
            handleClose={() => {
              setDateLimitPopup(false);
            }}
          />
        )
      }
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div >
  );
}

export default withConsoleBase(HSNSummaryReport);
